import type { QueryParams } from '@api/query';
import type { PaginatedResult } from '@api/resources/resource';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, onBeforeMount, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useRouter } from 'vue-router';

interface QueryParamsClass<P extends QueryParams> {
  new (): P;
  init: (options: { defaults?: Record<string, any>; json?: Record<string, any> }) => P;
}

export function computeEffectiveParams<P extends QueryParams>(
  providedParams: ComputedRef<P | null> | undefined,
  paramsClass: QueryParamsClass<P>,
  defaults: Record<string, any>,
): ComputedRef<P> {
  return computed<P>(() => {
    if (providedParams?.value) {
      return providedParams.value;
    }
    return paramsClass.init({ defaults }) as P;
  });
}

export interface UseResourceNavigationConfig<T extends { id: string }, P extends QueryParams> {
  detailRouteName: string;
  fetchList: (params: Record<string, any>) => Promise<PaginatedResult<T>>;
  params?: ComputedRef<P | null>;
  paramsClass: QueryParamsClass<P>;
  queryKeyPrefix: string;
}

export interface UseResourceNavigationReturn {
  canNavigateNext: ComputedRef<boolean>;
  canNavigatePrevious: ComputedRef<boolean>;
  isLoadingNavigation: Ref<boolean>;
  navigateToNext: () => Promise<void>;
  navigateToPrevious: () => Promise<void>;
}

export function useResourceNavigation<T extends { id: string }, P extends QueryParams>(
  resourceId: ComputedRef<string>,
  config: UseResourceNavigationConfig<T, P>,
): UseResourceNavigationReturn {
  const queryClient = useQueryClient();
  const router = useRouter();

  const resourceIds = ref<string[]>([]);
  const currentPage = ref<number>(0);
  const nextPage = ref<number | null>(null);
  const isLoadingNavigation = ref(false);
  const isInitialLoad = ref(true);

  const navigationQueryKey = computed(() => {
    if (!config.params?.value) return null;
    const params = config.params.value.serialize({ forApiRequest: true });
    return [config.queryKeyPrefix, JSON.parse(JSON.stringify(params))];
  });

  // Always fetch fresh to get pagination metadata (current_page, next_page)
  const updateResourceIds = async () => {
    if (!navigationQueryKey.value || !config.params?.value) {
      resourceIds.value = [];
      return;
    }

    isLoadingNavigation.value = true;
    try {
      const response = await config.fetchList(
        config.params.value.serialize({ forApiRequest: true }),
      );

      resourceIds.value = response.data.map((item) => item.id);
      currentPage.value = response.current_page;
      nextPage.value = response.next_page;

      queryClient.setQueryData(navigationQueryKey.value, response.data);
      checkAndNavigateIfNotInResults();
    } catch (error) {
      console.error('Failed to load navigation context:', error);
      resourceIds.value = [];
    } finally {
      isLoadingNavigation.value = false;
    }
  };

  // Navigate to first resource if current one is not in filtered results
  const checkAndNavigateIfNotInResults = () => {
    if (isInitialLoad.value) {
      return;
    }

    const currentInResults = resourceIds.value.includes(resourceId.value);

    if (!currentInResults && resourceIds.value.length > 0) {
      const firstId = resourceIds.value[0];
      router.push({
        name: config.detailRouteName,
        params: { id: firstId },
        query: config.params?.value ? config.params.value.serialize() : {},
      });
    }
  };

  const currentIndex = computed(() => {
    return resourceIds.value.indexOf(resourceId.value);
  });

  // Can navigate if there are items in the list or pages to load
  const canNavigatePrevious = computed(() => {
    return !(currentIndex.value === 0 && currentPage.value === 0);
  });

  const canNavigateNext = computed(() => {
    const isLastItem = currentIndex.value === resourceIds.value.length - 1;
    return !isLastItem || nextPage.value !== null;
  });

  const loadNextPage = async (): Promise<void> => {
    if (!config.params?.value || nextPage.value === null) return;

    const nextPageParams = config.paramsClass.init({
      json: config.params.value.serialize(),
    });
    (nextPageParams as any)._page = nextPage.value;

    isLoadingNavigation.value = true;
    try {
      const response = await config.fetchList(nextPageParams.serialize({ forApiRequest: true }));

      queryClient.setQueryData(
        [config.queryKeyPrefix, nextPageParams.serialize({ forApiRequest: true })],
        response.data,
      );

      resourceIds.value = response.data.map((item) => item.id);
      currentPage.value = response.current_page;
      nextPage.value = response.next_page;
    } catch (error) {
      console.error('Failed to fetch next page:', error);
    } finally {
      isLoadingNavigation.value = false;
    }
  };

  const loadPreviousPage = async (): Promise<void> => {
    if (!config.params?.value || currentPage.value === 0) return;

    const prevPageParams = config.paramsClass.init({
      json: config.params.value.serialize(),
    });
    (prevPageParams as any)._page = currentPage.value - 1;

    isLoadingNavigation.value = true;
    try {
      const response = await config.fetchList(prevPageParams.serialize({ forApiRequest: true }));

      queryClient.setQueryData(
        [config.queryKeyPrefix, prevPageParams.serialize({ forApiRequest: true })],
        response.data,
      );

      resourceIds.value = response.data.map((item) => item.id);
      currentPage.value = response.current_page;
      nextPage.value = response.next_page;
    } catch (error) {
      console.error('Failed to fetch previous page:', error);
    } finally {
      isLoadingNavigation.value = false;
    }
  };

  const navigateToPrevious = async () => {
    const currentIdx = currentIndex.value;

    if (currentIdx > 0) {
      const previousId = resourceIds.value[currentIdx - 1];
      router.push({
        name: config.detailRouteName,
        params: { id: previousId },
        query: config.params?.value ? config.params.value.serialize() : {},
      });
      return;
    }

    if (currentPage.value > 0) {
      await loadPreviousPage();
      if (resourceIds.value.length > 0) {
        const lastId = resourceIds.value[resourceIds.value.length - 1];
        router.push({
          name: config.detailRouteName,
          params: { id: lastId },
          query: config.params?.value ? config.params.value.serialize() : {},
        });
      }
    }
  };

  const navigateToNext = async () => {
    const currentIdx = currentIndex.value;

    if (currentIdx !== -1 && currentIdx < resourceIds.value.length - 1) {
      const nextId = resourceIds.value[currentIdx + 1];
      router.push({
        name: config.detailRouteName,
        params: { id: nextId },
        query: config.params?.value ? config.params.value.serialize() : {},
      });
      return;
    }

    if (nextPage.value !== null) {
      await loadNextPage();
      if (resourceIds.value.length > 0) {
        const firstId = resourceIds.value[0];
        router.push({
          name: config.detailRouteName,
          params: { id: firstId },
          query: config.params?.value ? config.params.value.serialize() : {},
        });
      }
    }
  };

  onBeforeMount(async () => {
    await updateResourceIds();
    isInitialLoad.value = false;
  });

  watch(
    navigationQueryKey,
    async (newKey, oldKey) => {
      if (JSON.stringify(newKey) !== JSON.stringify(oldKey)) {
        await updateResourceIds();
      }
    },
    { deep: true },
  );

  return {
    canNavigateNext,
    canNavigatePrevious,
    isLoadingNavigation,
    navigateToNext,
    navigateToPrevious,
  };
}
